import { useState, useEffect, useCallback, useRef } from "react";
import { api, formatApiError, API_BASE } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Wallet, Loader2, FileText, Receipt, Upload, CircleCheck, Printer, Download, Send, ChevronDown, ChevronUp, FileSignature } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { idr, fmtDateTime } from "../../lib/format";
import { cn } from "../../lib/utils";

const TERM_STATUS = {
  pending: { label: "Pending", cls: "bg-muted text-muted-foreground" },
  paid: { label: "Lunas", cls: "bg-[#10B981]/10 text-[#10B981]" },
};

const INV_STATUS = {
  draft: { label: "Draft", cls: "bg-muted text-muted-foreground" },
  sent: { label: "Terkirim", cls: "bg-[#1B2A6B]/10 text-[#1B2A6B] dark:text-blue-300" },
  paid: { label: "Lunas", cls: "bg-[#10B981]/10 text-[#10B981]" },
};

const SPK_STATUS = {
  draft: { label: "Draft", cls: "bg-muted text-muted-foreground" },
  pending_approval: { label: "Pending", cls: "bg-amber-500/10 text-amber-600 dark:text-amber-400" },
  approved: { label: "Approved", cls: "bg-[#10B981]/10 text-[#10B981]" },
  rejected: { label: "Rejected", cls: "bg-[#E31E24]/10 text-[#E31E24]" },
};

const FILTERS = [["all", "Semua"], ["pending", "Belum Lunas"], ["paid", "Lunas"], ["overdue", "Jatuh Tempo"]];

const EMPTY_FORM = { label: "", mode: "percent", percent: "", amount: "", due_date: "", progress_requirement: "", notes: "" };

const INPUT_CLS = "w-full h-9 px-3 text-sm rounded-md border border-border bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary";

function TermFormDialog({ initial, spk, contractValue, onClose, onSave, testid }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const computed = form.mode === "percent" ? Math.round(((Number(form.percent) || 0) / 100) * (contractValue || 0)) : (Number(form.amount) || 0);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) { toast.error("Label is required"); return; }
    setSaving(true);
    try {
      await onSave({
        label: form.label, mode: form.mode,
        percent: Number(form.percent) || 0, amount: Number(form.amount) || 0,
        due_date: form.due_date || null, progress_requirement: Number(form.progress_requirement) || 0,
        notes: form.notes, spk_id: spk?.id ?? initial?.spk_id ?? null,
      });
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md bg-background" data-testid={`${testid}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-heading">
            {initial ? "Edit Termin" : spk ? `Tambah Termin — SPK ${spk.spk_number || spk.title}` : "Tambah Termin (tanpa SPK)"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Label *</label>
            <input value={form.label} onChange={set("label")} placeholder="e.g. DP 30%, Progress 50%, Pelunasan" data-testid={`${testid}-label-input`} className={INPUT_CLS} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Amount mode</label>
            <div className="flex gap-1.5" data-testid={`${testid}-mode-toggle`}>
              {[["percent", "Percentage of contract"], ["fixed", "Fixed amount (IDR)"]].map(([m, label]) => (
                <button type="button" key={m} data-testid={`${testid}-mode-${m}`} onClick={() => setForm((f) => ({ ...f, mode: m }))}
                  className={cn("flex-1 h-8 text-xs rounded-md transition-colors", form.mode === m ? "bg-[#1B2A6B] text-white font-medium" : "border border-border text-muted-foreground hover:bg-muted")}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">{form.mode === "percent" ? "Percent (%)" : "Amount (IDR)"}</label>
              {form.mode === "percent" ? (
                <input type="number" min="0" max="100" value={form.percent} onChange={set("percent")} data-testid={`${testid}-percent-input`} className={INPUT_CLS} />
              ) : (
                <input type="number" min="0" value={form.amount} onChange={set("amount")} data-testid={`${testid}-amount-input`} className={INPUT_CLS} />
              )}
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Due date</label>
              <input type="date" value={form.due_date} onChange={set("due_date")} data-testid={`${testid}-due-input`} className={INPUT_CLS} />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground" data-testid={`${testid}-amount-preview`}>
            Jumlah: <span className="font-semibold text-foreground">{idr(computed)}</span>
            {form.mode === "percent" && <span> — dihitung dari nilai kontrak {spk ? "SPK ini" : "proyek"} ({idr(contractValue || 0)})</span>}
          </p>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Progress requirement (%) — optional</label>
            <input type="number" min="0" max="100" value={form.progress_requirement} onChange={set("progress_requirement")} data-testid={`${testid}-progress-req-input`} className={INPUT_CLS} placeholder="Minimum approved progress before payment" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Notes</label>
            <textarea rows={2} value={form.notes} onChange={set("notes")} data-testid={`${testid}-notes-input`}
              className="w-full px-3 py-2 text-sm rounded-md border border-border bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary resize-none" />
          </div>
          <DialogFooter>
            <button type="button" data-testid={`${testid}-cancel`} onClick={onClose} className="h-8 px-3 text-sm rounded-md border border-border hover:bg-muted transition-colors">Cancel</button>
            <button type="submit" data-testid={`${testid}-save`} disabled={saving}
              className="h-8 px-4 text-sm rounded-md bg-[#E31E24] hover:bg-[#C81A1F] text-white font-medium transition-colors disabled:opacity-60 flex items-center gap-1.5">
              {saving && <Loader2 size={13} className="animate-spin" />}Save
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DocPreviewDialog({ docId, title, onClose }) {
  const url = `${API_BASE}/invoices/${docId}/pdf`;
  const frameRef = useRef(null);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-4xl bg-background h-[88vh] flex flex-col" data-testid="doc-preview-dialog">
        <DialogHeader><DialogTitle className="font-heading">{title}</DialogTitle></DialogHeader>
        <iframe ref={frameRef} src={url} title="Document preview" className="flex-1 w-full rounded-md border border-border bg-white" data-testid="doc-preview-frame" />
        <DialogFooter className="gap-2">
          <button data-testid="doc-preview-close" onClick={onClose}
            className="h-8 px-3 text-sm rounded-md border border-border hover:bg-muted transition-colors">Tutup</button>
          <button data-testid="doc-preview-print" onClick={() => frameRef.current?.contentWindow?.print()}
            className="h-8 px-3 flex items-center gap-1.5 text-sm rounded-md border border-border hover:bg-muted transition-colors">
            <Printer size={14} />Print
          </button>
          <a data-testid="doc-preview-download" href={`${url}?download=1`} download
            className="h-8 px-3 flex items-center gap-1.5 text-sm rounded-md bg-[#1B2A6B] text-white hover:bg-[#16224f] transition-colors">
            <Download size={14} />Download PDF
          </a>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PayDialog({ term, onClose, onPaid }) {
  const [form, setForm] = useState({ payment_date: new Date().toISOString().slice(0, 10), payment_method: "Transfer Bank", notes: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/terms/${term.id}/pay`, form);
      toast.success(`"${term.label}" lunas — Kuitansi ${data.kuitansi?.doc_number || ""} dibuat otomatis`);
      onPaid();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm bg-background" data-testid="pay-dialog">
        <DialogHeader><DialogTitle className="font-heading">Konfirmasi Pembayaran</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="rounded-md bg-muted/50 p-3 text-xs space-y-1" data-testid="pay-summary">
            <p className="flex justify-between"><span className="text-muted-foreground">Termin</span><span className="font-medium">{term.label}</span></p>
            <p className="flex justify-between"><span className="text-muted-foreground">Jumlah diterima</span><span className="font-bold text-[#1B2A6B] dark:text-blue-300">{idr(term.amount)}</span></p>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Tanggal pembayaran</label>
            <input type="date" value={form.payment_date} onChange={set("payment_date")} data-testid="pay-date-input" className={INPUT_CLS} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Metode pembayaran</label>
            <select value={form.payment_method} onChange={set("payment_method")} data-testid="pay-method-select" className={INPUT_CLS}>
              <option value="Transfer Bank">Transfer Bank</option>
              <option value="Cash">Cash / Tunai</option>
              <option value="Cek / Giro">Cek / Giro</option>
              <option value="Lainnya">Lainnya</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Catatan (opsional)</label>
            <textarea rows={2} value={form.notes} onChange={set("notes")} data-testid="pay-notes-input"
              className="w-full px-3 py-2 text-sm rounded-md border border-border bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary resize-none" />
          </div>
          <p className="text-[11px] text-muted-foreground">Kuitansi akan dibuat otomatis setelah pembayaran dikonfirmasi.</p>
          <DialogFooter>
            <button type="button" data-testid="pay-cancel" onClick={onClose} className="h-8 px-3 text-sm rounded-md border border-border hover:bg-muted transition-colors">Batal</button>
            <button type="submit" data-testid="pay-submit" disabled={saving}
              className="h-8 px-4 text-sm rounded-md bg-[#10B981] hover:bg-[#0da271] text-white font-medium transition-colors disabled:opacity-60 flex items-center gap-1.5">
              {saving && <Loader2 size={13} className="animate-spin" />}Konfirmasi Lunas
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TermCard({ term, canManage, creating, onPay, onEdit, onDelete, onInvoice, onSend, onPreview, onUpload }) {
  const st = TERM_STATUS[term.status] || TERM_STATUS.pending;
  const inv = term.invoice_id ? INV_STATUS[term.invoice_status] || INV_STATUS.draft : null;
  const today = new Date().toISOString().slice(0, 10);
  const overdue = term.status !== "paid" && term.due_date && term.due_date < today;
  return (
    <div data-testid={`term-card-${term.id}`} className="rounded-lg border border-border bg-card p-4 space-y-2.5">
      <div className="flex items-start gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{term.label}</p>
          <p className="text-xs text-muted-foreground">
            {term.mode === "percent" ? `${term.percent}% of contract` : "Fixed amount"}
            {term.due_date ? ` · due ${term.due_date}` : ""}
            {term.progress_requirement ? ` · requires ${term.progress_requirement}% progress` : ""}
          </p>
        </div>
        <span className="text-sm font-bold shrink-0">{idr(term.amount)}</span>
        {overdue && (
          <span className="text-[10px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 shrink-0 bg-[#E31E24]/10 text-[#E31E24]" data-testid={`term-overdue-${term.id}`}>Jatuh Tempo</span>
        )}
        <span className={cn("text-[10px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 shrink-0", st.cls)} data-testid={`term-status-${term.id}`}>{st.label}</span>
      </div>
      {term.status === "paid" && (
        <p className="text-xs text-[#10B981]" data-testid={`term-paid-at-${term.id}`}>
          Lunas {term.payment_date || fmtDateTime(term.paid_at)}{term.payment_method ? ` · ${term.payment_method}` : ""}
          {term.kuitansi_number ? ` · Kuitansi ${term.kuitansi_number}` : ""}
        </p>
      )}
      {term.notes && <p className="text-xs text-muted-foreground">{term.notes}</p>}
      <div className="flex items-center gap-1.5 pt-1 border-t border-border flex-wrap">
        {term.invoice_id ? (
          <>
            <button data-testid={`term-invoice-view-${term.id}`} onClick={() => onPreview(term.invoice_id, `Invoice ${term.invoice_number}`)}
              className={cn("h-7 px-2.5 flex items-center gap-1 text-[11px] rounded-md border font-medium transition-colors hover:bg-muted", inv.cls)}>
              <FileText size={11} />INV {term.invoice_number} · {inv.label}
            </button>
            {term.invoice_status === "draft" && (
              <button data-testid={`term-invoice-send-${term.id}`} onClick={() => onSend(term)}
                className="h-7 px-2 flex items-center gap-1 text-[11px] rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                <Send size={11} />Tandai Terkirim
              </button>
            )}
          </>
        ) : (
          <button data-testid={`term-invoice-create-${term.id}`} onClick={() => onInvoice(term)} disabled={creating}
            className="h-8 px-3 flex items-center gap-1.5 text-xs font-medium rounded-md bg-[#1B2A6B] text-white hover:bg-[#16224f] transition-colors disabled:opacity-60">
            {creating ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}Buat Invoice
          </button>
        )}
        {term.kuitansi_id && (
          <button data-testid={`term-receipt-view-${term.id}`} onClick={() => onPreview(term.kuitansi_id, `Kuitansi ${term.kuitansi_number}`)}
            className="h-7 px-2.5 flex items-center gap-1 text-[11px] rounded-md border border-[#10B981]/40 text-[#10B981] font-medium hover:bg-[#10B981]/5 transition-colors">
            <Receipt size={11} />KWI {term.kuitansi_number}
          </button>
        )}
        {term.proof_attachment && (
          <a href={`${API_BASE}/files/${term.proof_attachment.path}`} target="_blank" rel="noreferrer" data-testid={`term-doc-view-proof-${term.id}`}
            className="h-7 px-2 flex items-center gap-1 text-[11px] rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
            <CircleCheck size={11} />Proof
          </a>
        )}
        <button data-testid={`term-upload-proof-${term.id}`} onClick={() => onUpload(term.id)} className="h-7 px-2 flex items-center gap-1 text-[11px] rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"><Upload size={11} />Bukti Bayar</button>
        {term.status !== "paid" && (
          <button data-testid={`term-pay-${term.id}`} onClick={() => onPay(term)}
            className="h-8 px-3 flex items-center gap-1 text-xs font-medium rounded-md bg-[#10B981] text-white hover:bg-[#0da271] transition-colors">
            <CircleCheck size={12} />Mark paid
          </button>
        )}
        {term.status !== "paid" && (
          <button data-testid={`term-edit-${term.id}`} onClick={() => onEdit(term)} className="h-7 px-2 flex items-center gap-1 text-xs rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"><Pencil size={12} />Edit</button>
        )}
        {canManage && (
          <button data-testid={`term-delete-${term.id}`} onClick={() => onDelete(term)} className="h-7 px-2 flex items-center gap-1 text-xs rounded-md hover:bg-[#E31E24]/10 text-muted-foreground hover:text-[#E31E24] transition-colors ml-auto"><Trash2 size={12} />Delete</button>
        )}
      </div>
    </div>
  );
}

export default function TermsSection({ project }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(null); // {mode:'create', spk} | {mode:'edit', term, spk}
  const [statusFilter, setStatusFilter] = useState("all");
  const [spkFilter, setSpkFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("");
  const [collapsed, setCollapsed] = useState({});
  const [preview, setPreview] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [creatingId, setCreatingId] = useState(null);
  const uploadRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null);
  const canManage = user?.role === "admin" || project.created_by === user?.id;
  const today = new Date().toISOString().slice(0, 10);

  const load = useCallback(async () => {
    const { data } = await api.get(`/projects/${project.id}/payments`);
    setData(data);
  }, [project.id]);

  useEffect(() => { load().catch((e) => toast.error(formatApiError(e))); }, [load]);

  const save = async (form) => {
    if (dialog.mode === "create") await api.post(`/projects/${project.id}/terms`, form);
    else await api.patch(`/terms/${dialog.term.id}`, form);
    toast.success("Payment term saved");
    load();
  };

  const remove = async (term) => {
    if (!window.confirm(`Delete term "${term.label}"?`)) return;
    try {
      await api.delete(`/terms/${term.id}`);
      toast.success("Payment term deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createInvoice = async (term) => {
    setCreatingId(term.id);
    try {
      const { data } = await api.post(`/terms/${term.id}/invoice`, {});
      toast.success(`Invoice ${data.doc_number} dibuat otomatis dari SPK/RAB/Termin`);
      await load();
      setPreview({ id: data.id, title: `Invoice ${data.doc_number}` });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreatingId(null); }
  };

  const markSent = async (term) => {
    try {
      await api.patch(`/invoices/${term.invoice_id}`, { status: "sent" });
      toast.success("Invoice ditandai terkirim");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const pickUpload = (termId) => {
    setUploadTarget(termId);
    uploadRef.current?.click();
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !uploadTarget) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/terms/${uploadTarget}/documents?kind=proof`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Bukti bayar uploaded");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadTarget(null); }
  };

  const filterTerms = (terms) => terms.filter((t) => {
    if (statusFilter === "paid") return t.status === "paid";
    if (statusFilter === "overdue") return t.status !== "paid" && t.due_date && t.due_date < today;
    if (statusFilter === "pending") return t.status !== "paid";
    return true;
  });

  const termProps = (sectionSpk) => ({
    canManage,
    onPay: setPayTarget,
    onEdit: (term) => setDialog({ mode: "edit", term, spk: sectionSpk }),
    onDelete: remove,
    onInvoice: createInvoice,
    onSend: markSent,
    onPreview: (id, title) => setPreview({ id, title }),
    onUpload: pickUpload,
  });

  if (data === null) {
    return <div className="flex justify-center py-10"><div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;
  }

  const sections = (data.sections || []).filter((s) =>
    (spkFilter === "all" || s.spk.id === spkFilter) && (!clientFilter || s.spk.client === clientFilter));
  const clients = [...new Set((data.sections || []).map((s) => s.spk.client).filter(Boolean))];
  const totals = (data.sections || []).concat(data.unlinked ? [{ summary: data.unlinked.summary }] : [])
    .reduce((acc, s) => ({
      contract: acc.contract + s.summary.contract_value,
      paid: acc.paid + s.summary.paid,
      outstanding: acc.outstanding + s.summary.outstanding,
      invoiced: acc.invoiced + s.summary.invoiced,
    }), { contract: 0, paid: 0, outstanding: 0, invoiced: 0 });

  const renderSection = (key, spk, summary, terms) => {
    const isOpen = !collapsed[key];
    const st = spk ? SPK_STATUS[spk.status] || SPK_STATUS.draft : null;
    const visTerms = filterTerms(terms);
    return (
      <section key={key} className="rounded-lg border border-border bg-card overflow-hidden" data-testid={`payment-section-${key}`}>
        <button type="button" onClick={() => setCollapsed((c) => ({ ...c, [key]: !c[key] }))} data-testid={`payment-section-toggle-${key}`}
          className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-muted/40 transition-colors">
          <FileSignature size={15} className="text-[#1B2A6B] dark:text-blue-300 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">
              {spk ? `SPK ${spk.spk_number || "-"} — ${spk.title}` : "Tanpa SPK (data lama / manual)"}
            </p>
            <p className="text-[11px] text-muted-foreground truncate">
              {spk?.client || ""}{spk?.client ? " · " : ""}{summary.term_count} termin · {summary.paid_count} lunas
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-3 shrink-0 text-[11px]">
            {spk && <span className="text-muted-foreground">Kontrak <span className="font-semibold text-foreground">{idr(summary.contract_value)}</span></span>}
            <span className="text-muted-foreground">Terbayar <span className="font-semibold text-[#10B981]">{idr(summary.paid)}</span></span>
            {spk && <span className="text-muted-foreground">Sisa <span className="font-semibold text-[#E31E24]">{idr(summary.outstanding)}</span></span>}
          </div>
          {st && <span className={cn("text-[10px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 shrink-0", st.cls)} data-testid={`payment-spk-status-${key}`}>{st.label}</span>}
          {isOpen ? <ChevronUp size={15} className="text-muted-foreground shrink-0" /> : <ChevronDown size={15} className="text-muted-foreground shrink-0" />}
        </button>
        {isOpen && (
          <div className="border-t border-border p-4 space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid={`payment-summary-${key}`}>
              {[
                ["Nilai Kontrak", spk ? idr(summary.contract_value) : "—", "text-foreground"],
                ["Commitment Fee", spk ? idr(spk.commitment_fee || 0) : "—", "text-[#10B981]"],
                ["Invoice", idr(summary.invoiced), "text-blue-600 dark:text-blue-400"],
                ["Terbayar", idr(summary.paid), "text-[#10B981]"],
                ["Sisa Tagihan", spk ? idr(summary.outstanding) : "—", "text-[#E31E24]"],
                ["Jatuh Tempo", idr(summary.overdue_amount), "text-amber-600 dark:text-amber-400"],
              ].map(([label, val, cls]) => (
                <div key={label} className="rounded-md bg-muted/40 px-2.5 py-2">
                  <p className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</p>
                  <p className={cn("text-xs font-bold mt-0.5 truncate", cls)}>{val}</p>
                </div>
              ))}
            </div>
            {visTerms.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4" data-testid={`payment-empty-${key}`}>
                {terms.length === 0 ? "Belum ada termin untuk kontrak ini." : "Tidak ada termin pada filter ini."}
              </p>
            ) : (
              <div className="space-y-2.5">
                {visTerms.map((t) => <TermCard key={t.id} term={t} creating={creatingId === t.id} {...termProps(spk)} />)}
              </div>
            )}
            <button type="button" data-testid={`term-add-button-${key}`} onClick={() => setDialog({ mode: "create", spk })}
              className="h-8 px-3 flex items-center gap-1.5 text-xs font-medium rounded-md border border-dashed border-border hover:bg-muted transition-colors w-full justify-center">
              <Plus size={13} />Tambah Termin {spk ? "untuk SPK ini" : "(tanpa SPK)"}
            </button>
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="space-y-4" data-testid="terms-section">
      <div className="flex items-center gap-3 flex-wrap">
        <h3 className="font-heading text-base font-semibold flex items-center gap-2"><Wallet size={16} className="text-[#1B2A6B] dark:text-blue-300" />Payments per SPK</h3>
        <p className="text-[11px] text-muted-foreground ml-auto" data-testid="payments-hint">Setiap SPK memiliki pembayaran terpisah — tidak digabung antar kontrak.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="terms-summary">
        {[
          ["Total Kontrak", totals.contract, "text-foreground"],
          ["Total Invoice", totals.invoiced, "text-blue-600 dark:text-blue-400"],
          ["Total Terbayar", totals.paid, "text-[#10B981]"],
          ["Total Sisa", totals.outstanding, "text-[#E31E24]"],
        ].map(([label, val, cls]) => (
          <div key={label} className="rounded-lg border border-border bg-card p-3" data-testid={`terms-summary-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
            <p className={cn("text-sm font-bold mt-0.5 truncate", cls)}>{idr(val)}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="payments-filters">
        <div className="flex gap-1.5 flex-wrap" data-testid="terms-filters">
          {FILTERS.map(([k, label]) => (
            <button key={k} data-testid={`terms-filter-${k}`} onClick={() => setStatusFilter(k)}
              className={cn("h-7 px-3 text-xs rounded-full border transition-colors",
                statusFilter === k ? "bg-[#1B2A6B] text-white border-[#1B2A6B] font-medium" : "border-border text-muted-foreground hover:bg-muted")}>
              {label}
            </button>
          ))}
        </div>
        <select value={spkFilter} onChange={(e) => setSpkFilter(e.target.value)} data-testid="payments-filter-spk"
          className="h-7 px-2 text-xs rounded-md border border-border bg-transparent max-w-[180px]">
          <option value="all">Semua SPK</option>
          {(data.sections || []).map((s) => <option key={s.spk.id} value={s.spk.id}>SPK {s.spk.spk_number || s.spk.title}</option>)}
          {data.unlinked && <option value="unlinked">Tanpa SPK</option>}
        </select>
        <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} data-testid="payments-filter-client"
          className="h-7 px-2 text-xs rounded-md border border-border bg-transparent max-w-[180px]">
          <option value="">Semua Klien</option>
          {clients.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="space-y-3">
        {spkFilter !== "unlinked" && sections.map((s) => renderSection(s.spk.id, s.spk, s.summary, s.terms))}
        {data.unlinked && !clientFilter && (spkFilter === "all" || spkFilter === "unlinked")
          ? renderSection("unlinked", null, data.unlinked.summary, data.unlinked.terms)
          : null}
        {sections.length === 0 && !data.unlinked && (
          <div className="text-center py-12 text-muted-foreground border border-dashed border-border rounded-lg" data-testid="terms-empty">
            <Wallet size={26} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">Belum ada SPK pada proyek ini. Buat SPK terlebih dahulu — termin pembayaran dibuat otomatis saat SPK disetujui.</p>
          </div>
        )}
      </div>

      <input type="file" ref={uploadRef} className="hidden" data-testid="term-doc-upload-input" onChange={onUpload} />
      {dialog && (
        <TermFormDialog testid="term-form" initial={dialog.mode === "edit" ? dialog.term : null} spk={dialog.spk}
          contractValue={dialog.spk ? (data.sections.find((s) => s.spk.id === dialog.spk.id)?.summary.contract_value ?? 0) : 0}
          onClose={() => setDialog(null)} onSave={save} />
      )}
      {preview && <DocPreviewDialog docId={preview.id} title={preview.title} onClose={() => setPreview(null)} />}
      {payTarget && <PayDialog term={payTarget} onClose={() => setPayTarget(null)} onPaid={load} />}
    </div>
  );
}
